import { isDeclinableJobType } from '../enums/job-type.enum.js'
import type { TreeJobDto } from '../dto/tree.dto.js'

/** One job on one tree, as far as time is concerned. */
export type JobWithMinutes = Pick<TreeJobDto, 'type' | 'minutes'>

/**
 * Whether a tree's time has been split across its jobs.
 *
 * All or nothing, which the input schema enforces: half-itemized would mean
 * the parts do not add up to the whole, and a client declining one job would
 * be taking away a figure that was never part of what they were charged.
 */
export function isItemized(jobs: JobWithMinutes[]): boolean {
  return jobs.length > 0 && jobs.every((job) => job.minutes !== null)
}

/**
 * The tree's time when it has been itemized, or null when it has not.
 *
 * This is the tree's estimate rather than a second opinion about it. Once a
 * tree is itemized the estimate IS the sum — the field is derived and shown
 * read-only — because two numbers that can disagree eventually will, and the
 * one a client was charged against would be whichever the renderer happened
 * to read.
 */
export function itemizedMinutes(jobs: JobWithMinutes[]): number | null {
  if (!isItemized(jobs)) return null
  return jobs.reduce((sum, job) => sum + (job.minutes ?? 0), 0)
}

/**
 * The minutes a client can decline on this tree.
 *
 * Everything itemized except OTHER, which is access, setup and clearing up —
 * what has to happen for the crew to be on the property at all. A tree that
 * was never itemized returns nothing: without a per-job figure there is no
 * amount to take away, so all of its work stays in the price.
 */
export function declinableMinutes(jobs: JobWithMinutes[]): number {
  if (!isItemized(jobs)) return 0
  return jobs
    .filter((job) => isDeclinableJobType(job.type))
    .reduce((sum, job) => sum + (job.minutes ?? 0), 0)
}

/**
 * Whether a tree satisfies the itemization rule in force.
 *
 * A tree with no pruning jobs has nothing to split, so it always passes: the
 * rule is "if it carries work, say how the time divides", not "every tree
 * must have jobs". Otherwise a surveyor recording a healthy tree would be
 * asked to itemize nothing.
 */
export function meetsItemizationRule(
  jobs: JobWithMinutes[],
  isRequired: boolean,
): boolean {
  if (!isRequired) return true
  if (jobs.length === 0) return true
  return isItemized(jobs)
}
